import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import {
    ImagePlaceholder,
    DropAreaIndicator,
    useDragAndDropFileContext,
    INDICATION_TYPES,
    TooltipV2 as Tooltip,
    WarningCircleIcon,
    TOOLTIP_POSITION,
} from 'taboola-ultimate-ui';
import { defaultDropFileText } from '../../../../creative-creator/components/DropContainer/DropContainer';
import CloudinaryImageWithEditBanner from './CloudinaryImageWithEditBanner';
import ThumbnailEditorImageOverlay from './ThumbnailEditorImageOverlay';
import styles from './thumbnailEditorImage.module.scss';

const classNameBuilder = classnames.bind(styles);

const ThumbnailEditorImage = ({
    imageSrc,
    isThumbnailLoading,
    imageError,
    failedValidationData,
    scrollRef,
    openCropModal,
    disabled,
}) => {
    const { isDragActive } = useDragAndDropFileContext();

    const isErrorValidation = failedValidationData?.indicationType === INDICATION_TYPES.ERROR;
    const isWarningValidation = failedValidationData?.indicationType === INDICATION_TYPES.WARNING;

    const encodedImageSrc = encodeURIComponent(imageSrc);

    if (imageError) {
        return (
            <div className={styles['container']}>
                <ImagePlaceholder />
                <ThumbnailEditorImageOverlay isThumbnailLoading={isThumbnailLoading} imageError={imageError} />
            </div>
        );
    }

    return (
        <div
            className={classNameBuilder('container', {
                disabled,
                error: isErrorValidation,
                warning: isWarningValidation,
            })}
            ref={scrollRef}
            data-automation-value={imageSrc}
        >
            {isDragActive && <DropAreaIndicator text={defaultDropFileText} />}
            <CloudinaryImageWithEditBanner
                imageSrc={encodedImageSrc}
                width={styles.thumbnailWidth}
                height={styles.thumbnailHeight}
                editPermissions="CREATIVE_PREVIEW_VIEW"
                openCropModal={openCropModal}
            />
            <ThumbnailEditorImageOverlay isThumbnailLoading={isThumbnailLoading} />

            {(isErrorValidation || isWarningValidation) && (
                <div className={styles['badge-container']} aria-label="thumbnail-editor-image-validation-error">
                    <WarningCircleIcon
                        className={classNameBuilder('badge', {
                            error: isErrorValidation,
                            warning: isWarningValidation,
                        })}
                    />
                    <Tooltip position={TOOLTIP_POSITION.RIGHT}>{failedValidationData.message}</Tooltip>
                </div>
            )}
        </div>
    );
};

ThumbnailEditorImage.propTypes = {
    imageSrc: PropTypes.string,
    isThumbnailLoading: PropTypes.bool,
    imageError: PropTypes.shape({
        status: PropTypes.number,
    }),
    disabled: PropTypes.bool,
    failedValidationData: PropTypes.object,
    scrollRef: PropTypes.object,
};

export default ThumbnailEditorImage;
